import { Badge } from "@/core/components/ui/badge";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  DetailCloseButton,
  DetailSidebarFallback,
} from "@/accounts-payable/common";
import { useGetVendor, useUpdateVendor } from "@/accounts-payable/query";
import type {
  ApVendorUpdate,
  ApVendorListEdit,
} from "@/accounts-payable/types";
import { IncomingEmailSection } from "./incoming-email-section";
import { ProductLookupsSection } from "./product-lookups-section";
import { ServiceTitanSection } from "./service-titan-section";

/**
 * Vendor management rendered inside the shared SimpleSidebar. Looks the vendor
 * up by id and persists each change inline.
 */
export const ApVendorDetail = ({
  vendorId,
  businessId,
  onClose,
}: {
  vendorId: string;
  businessId: string | null;
  onClose: () => void;
}) => {
  const { data: vendor, isPending } = useGetVendor(businessId, vendorId);
  const update = useUpdateVendor();

  if (!vendor) {
    return (
      <DetailSidebarFallback
        loading={isPending}
        errorMessage="Couldn’t load the vendor. Please try again."
        onClose={onClose}
      />
    );
  }

  const patch = (changes: ApVendorUpdate) =>
    update.mutate({ businessId, vendorId: vendor.id, changes });
  const editList = (listEdit: ApVendorListEdit) =>
    update.mutate({ businessId, vendorId: vendor.id, listEdit });

  return (
    <Stack gap="none" fullWidth className="h-full">
      {/* Header. Like the table, only the exceptions get a badge; with none,
          the name moves up beside the close button rather than leaving an
          empty row above it. */}
      <Stack
        direction="row"
        justify="between"
        align="start"
        gap="md"
        fullWidth
        className="flex-none border-b border-stroke-tertiary bg-bg-secondary/50 p-6"
      >
        <Stack gap="sm">
          {(!vendor.st_vendor_id || !vendor.active) && (
            <Stack
              direction="row"
              gap="sm"
              align="center"
              className="flex-wrap"
            >
              {!vendor.st_vendor_id && (
                <Badge variant="gold" size="sm">
                  Not linked
                </Badge>
              )}
              {!vendor.active && (
                <Badge variant="gray" size="sm">
                  Inactive
                </Badge>
              )}
            </Stack>
          )}
          <Typography variant="subtitleW1">{vendor.name}</Typography>
        </Stack>
        <DetailCloseButton onClose={onClose} />
      </Stack>

      {/* Body: three sections by what the admin is doing, split by
          separators as in the Invoices panel. No cards inside the panel. */}
      <ScrollArea className="min-h-0 w-full flex-1">
        <Stack key={vendor.id} gap="lg" fullWidth className="px-6 py-6">
          <ServiceTitanSection vendor={vendor} />
          <Separator />

          <IncomingEmailSection
            vendor={vendor}
            onUpdate={patch}
            onEditList={editList}
          />
          <Separator />
          <ProductLookupsSection
            domains={vendor.allowed_domains ?? []}
            onEditList={editList}
          />
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
